import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuthStore } from "@/stores/authStore";
import { setRedirectFunction } from "@/services/apiService";
import { getEffectiveAuth } from "@/lib/authUtils";

/**
 * Componente que inicializa la aplicación:
 *  - Aplica el tema guardado en localStorage.
 *  - Verifica si existe un token válido; si no, redirige al login.
 *  - Configura la función de redirección para el servicio API.
 */
export function AppInitializer() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = useAuthStore((state) => state.token);
  const expiresAt = useAuthStore((state) => state.expiresAt);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const firstRun = useRef(true);

  // Aplica el tema guardado al <html>
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "default";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  // Configura la función de redirección para el servicio API
  useEffect(() => {
    setRedirectFunction(() => {
      navigate("/login", { replace: true });
    });
  }, [navigate]);

  // Verifica autenticación y expiración de token
  useEffect(() => {
    // En el primer render, decide inmediatamente usando localStorage para evitar parpadeos
    if (firstRun.current) {
      firstRun.current = false;
      const { tokenValid } = getEffectiveAuth({ token, expiresAt });
      if (tokenValid && location.pathname.startsWith("/login")) {
        navigate("/home", { replace: true });
        return;
      }
      if (!tokenValid && !location.pathname.startsWith("/login")) {
        navigate("/login", { replace: true });
        return;
      }
    }

    // Resto de ciclos: mantener coherencia al cambiar el store
    const { tokenValid } = getEffectiveAuth({ token, expiresAt });
    if (tokenValid) {
      if (location.pathname.startsWith("/login")) {
        navigate("/home", { replace: true });
      }
      return;
    }

    if (isAuthenticated) logout();
    if (!location.pathname.startsWith("/login")) {
      navigate("/login", { replace: true });
    }
  }, [token, expiresAt, isAuthenticated, logout, navigate, location.pathname]);

  return null;
}
